'use client';

import { c as _c } from "react/compiler-runtime";
import type { Post } from '@shared/all/domains/posts';
import { useUserIdentity } from '@shared/utils/context/UserContext';
import { formatNumbers } from '@shared/utils/helpers/formatNumbers';
import usePostsShared from '@shared/utils/hooks/usePosts';
import { Bookmark, Eye, MessageSquare } from 'lucide-react';
import { useRouter } from 'next/navigation';
import type React from 'react';
import { useState } from 'react';
import { client } from '../../utils/trpc';
import { useAuthModals } from '../../widgets/WidgetsContext';
import { NewPostModal } from './NewPost/NewPostModal';
import PostShareButton from './PostShareButton';
import Vote from './Vote';
type Props = {
  post: Post;
  nsfw: boolean;
  politics: boolean;
  troll?: boolean;
  setNsfw?: (arg0: boolean) => void;
  setPolitics?: (arg0: boolean) => void;
  setTroll?: (arg0: boolean) => void;
  isReply?: boolean;
  redirectOnReply?: boolean;
  uniqueId?: string;
  minified?: boolean;
  disableReply?: boolean;
};
const PostItemActions = props => {
  const $ = _c(66);
  const currentUser = useUserIdentity();
  const {
    openAuthContinueModal
  } = useAuthModals();
  const {
    savePostHandler
  } = usePostsShared(client, props.uniqueId);
  const router = useRouter();
  const [replyOpen, setReplyOpen] = useState(false);
  let t0;
  if ($[0] !== currentUser.isAuthenticated || $[1] !== openAuthContinueModal || $[2] !== props.disableReply) {
    t0 = e => {
      e.stopPropagation();
      if (props.disableReply) {
        return;
      }
      if (!currentUser.isAuthenticated) {
        return openAuthContinueModal();
      }
      setReplyOpen(_temp);
    };
    $[0] = currentUser.isAuthenticated;
    $[1] = openAuthContinueModal;
    $[2] = props.disableReply;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const replyButtonHandler = t0;
  let t1;
  if ($[4] !== currentUser.isAuthenticated || $[5] !== openAuthContinueModal || $[6] !== props.post.id || $[7] !== savePostHandler) {
    t1 = e_0 => {
      e_0.stopPropagation();
      if (!currentUser.isAuthenticated) {
        return openAuthContinueModal();
      }
      savePostHandler(props.post.id);
    };
    $[4] = currentUser.isAuthenticated;
    $[5] = openAuthContinueModal;
    $[6] = props.post.id;
    $[7] = savePostHandler;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const saveButtonHandler = t1;
  const iconSize = props.minified ? 14 : 18;
  const t2 = `flex items-center ${props.minified ? "justify-start gap-2" : "justify-between py-1"}`;
  let t3;
  if ($[9] !== props.minified || $[10] !== props.post.id || $[11] !== props.post.isPrivate || $[12] !== props.post.voteCount || $[13] !== props.post.voted || $[14] !== props.uniqueId) {
    t3 = <Vote disabled={props.post.isPrivate} voteCount={props.post.voteCount} postId={props.post.id} uniqueId={props.uniqueId} userVoted={props.post.voted} minified={props.minified} />;
    $[9] = props.minified;
    $[10] = props.post.id;
    $[11] = props.post.isPrivate;
    $[12] = props.post.voteCount;
    $[13] = props.post.voted;
    $[14] = props.uniqueId;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  const t4 = `flex items-center justify-center gap-1 rounded-md transition-all duration-200 hover:scale-[1.05] hover:bg-background-light active:scale-[0.95] ${props.minified ? "px-2 py-1" : "px-3 py-1"}`;
  let t5;
  if ($[16] !== iconSize) {
    t5 = <MessageSquare size={iconSize} className="text-textSecondary" />;
    $[16] = iconSize;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== props.post.replies_count) {
    t6 = formatNumbers(props.post.replies_count);
    $[18] = props.post.replies_count;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== t6) {
    t7 = <span className="text-xs text-textSecondary">{t6}</span>;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== replyButtonHandler || $[23] !== t4 || $[24] !== t5 || $[25] !== t7) {
    t8 = <button className={t4} onClick={replyButtonHandler}>{t5}{t7}</button>;
    $[22] = replyButtonHandler;
    $[23] = t4;
    $[24] = t5;
    $[25] = t7;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== props.minified || $[28] !== props.nsfw || $[29] !== props.politics || $[30] !== props.post) {
    t9 = <PostShareButton post={props.post} nsfw={props.nsfw} politics={props.politics} minified={props.minified} />;
    $[27] = props.minified;
    $[28] = props.nsfw;
    $[29] = props.politics;
    $[30] = props.post;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  const t10 = props.post.saved ? "Unsave" : "Save";
  const t11 = props.post.saved ? "fill-primary text-primary" : "text-textSecondary";
  let t12;
  if ($[32] !== iconSize || $[33] !== t11) {
    t12 = <Bookmark size={iconSize} className={t11} />;
    $[32] = iconSize;
    $[33] = t11;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] !== saveButtonHandler || $[36] !== t10 || $[37] !== t12) {
    t13 = <button title={t10} className="flex items-center justify-center rounded-md px-2 py-1 transition-transform duration-200 hover:scale-[1.05] hover:bg-background-light active:scale-[0.95]" onClick={saveButtonHandler}>{t12}</button>;
    $[35] = saveButtonHandler;
    $[36] = t10;
    $[37] = t12;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  const t14 = `${props.post.viewCount} views`;
  let t15;
  if ($[39] !== iconSize) {
    t15 = <Eye size={iconSize} />;
    $[39] = iconSize;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== props.post.viewCount) {
    t16 = formatNumbers(props.post.viewCount);
    $[41] = props.post.viewCount;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] !== t16) {
    t17 = <span className="text-xs">{t16}</span>;
    $[43] = t16;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== t14 || $[46] !== t15 || $[47] !== t17) {
    t18 = <div className="flex items-center gap-1 px-2 py-1 text-textSecondary" title={t14}>{t15}{t17}</div>;
    $[45] = t14;
    $[46] = t15;
    $[47] = t17;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== t13 || $[50] !== t18 || $[51] !== t2 || $[52] !== t3 || $[53] !== t8 || $[54] !== t9) {
    t19 = <div onClick={_temp2} className={t2}>{t3}{t8}{t9}{t13}{t18}</div>;
    $[49] = t13;
    $[50] = t18;
    $[51] = t2;
    $[52] = t3;
    $[53] = t8;
    $[54] = t9;
    $[55] = t19;
  } else {
    t19 = $[55];
  }
  let t20;
  if ($[56] !== props.isReply || $[57] !== props.post || $[58] !== props.redirectOnReply || $[59] !== props.uniqueId || $[60] !== replyOpen || $[61] !== router) {
    t20 = replyOpen && <div onClick={_temp3}><NewPostModal inline={Boolean(props.isReply)} parentPostId={props.post.id} parentPost={props.post} isPrivatePost={props.post.isPrivate} uniqueId={props.uniqueId} closeHandler={() => setReplyOpen(false)} onPostCreated={() => {
        if (props.redirectOnReply) {
          router.push(props.post.isPrivate ? `/private-post/${props.post.id}` : `/post/${props.post.id}`);
        }
        setReplyOpen(false);
      }} /></div>;
    $[56] = props.isReply;
    $[57] = props.post;
    $[58] = props.redirectOnReply;
    $[59] = props.uniqueId;
    $[60] = replyOpen;
    $[61] = router;
    $[62] = t20;
  } else {
    t20 = $[62];
  }
  let t21;
  if ($[63] !== t19 || $[64] !== t20) {
    t21 = <>{t19}{t20}</>;
    $[63] = t19;
    $[64] = t20;
    $[65] = t21;
  } else {
    t21 = $[65];
  }
  return t21;
};
export default PostItemActions;
function _temp(x) {
  return !x;
}
function _temp2(e_1) {
  return e_1.stopPropagation();
}
function _temp3(e_2) {
  return e_2.stopPropagation();
}